import { useState } from 'react';
import { Form, PasswordInput, minLength, required, useNotify, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Button, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import * as userService from '../../services/userService';

interface ResetPasswordPopupProps {
  id: string;
  username?: string;
  onClose: () => void;
}

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
  currentPassword: string;
}

function ResetPasswordContent({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  isLoading: boolean;
}) {
  const t = useTranslate();
  const { handleSubmit } = useFormContext();

  const matchesNewPassword = (value: string, allValues: ResetPasswordFormData) =>
    value === allValues?.newPassword ? undefined : t('module.user.passwordNotMatch');

  return (
    <>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <PasswordInput
            source="newPassword"
            label={t('module.user.newPassword')}
            fullWidth
            validate={[required(), minLength(8)]}
            autoComplete="new-password"
          />
          <PasswordInput
            source="confirmPassword"
            label={t('module.user.confirmPassword')}
            fullWidth
            validate={[required(), matchesNewPassword]}
            autoComplete="new-password"
          />
          <PasswordInput
            source="currentPassword"
            label={t('module.user.yourCurrentPassword')}
            fullWidth
            validate={required()}
            autoComplete="current-password"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('module.user.buttons.cancel')}</Button>
        <Button variant="contained" disabled={isLoading} onClick={handleSubmit(onSubmit)}>
          {t('module.user.resetPassword')}
        </Button>
      </DialogActions>
    </>
  );
}

// The server answers a refused action with 403 {code, message}; show its message and never log the
// caller out here (a wrong "current password" is REAUTH_FAILED, not an expired session).
const ResetPasswordPopup = ({ id, username, onClose }: ResetPasswordPopupProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    try {
      await userService.resetPassword(id, {
        newPassword: data.newPassword,
        currentPassword: data.currentPassword,
      });
      notify(t('module.user.resetPasswordSuccess'), { type: 'success' });
      onClose();
    } catch (error: any) {
      notify(error?.response?.data?.message ?? t('module.user.resetPasswordFailed'), {
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('module.user.resetPassword')}
        {username ? ` - ${username}` : ''}
      </DialogTitle>
      <Form onSubmit={handleSubmit as any}>
        <ResetPasswordContent onClose={onClose} onSubmit={handleSubmit as any} isLoading={isLoading} />
      </Form>
    </>
  );
};

export default ResetPasswordPopup;
